import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// S3 Phase 3.6 (spec §3.7) — 우측 슬라이드 드로어.
//
// Modal 과 동일한 a11y 규약을 따른다 (ESC 닫기, 오버레이 클릭 닫기,
// 포커스 트랩, 포커스 복원, portal 렌더). 중앙 모달과 달리 화면 우측에서
// 슬라이드되어 나오는 측면 패널로, 본문 맥락(예: 서명 발송 목록)을
// 유지한 채 보조 작업(양식 CRUD)을 노출하는 데 적합하다.

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Panel width in px. Defaults to a roomy 720 so list+table bodies fit. */
  width?: number;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Drawer({ open, onClose, title, children, width = 720 }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // See Modal.tsx — keep latest onClose in a ref so the focus effect depends
  // only on `open`. Parents pass an inline onClose, so depending on it re-ran
  // this effect every keystroke and rAF stole focus to the × button, bouncing
  // the caret out of inputs in drawer forms (e.g. sign-template CRUD).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Focus the first form control on open (prefer inputs/selects over the ×
    // close button); fall back to any focusable for a11y.
    window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target =
        panel.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? panel.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    });

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      // Length-checked above, so both are present — narrow for
      // noUncheckedIndexedAccess.
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus?.();
    };
    // Depends only on `open` — see onCloseRef note above.
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg-overlay)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 60,
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        style={panel(width)}
      >
        <header style={drawerHeader}>
          <div id="drawer-title" style={{ fontSize: 15, fontWeight: 600 }}>
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'transparent',
              fontSize: 18,
              cursor: 'pointer',
              color: 'var(--fg-muted)',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </header>
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function panel(width: number): CSSProperties {
  return {
    width,
    maxWidth: 'calc(100vw - 48px)',
    height: '100vh',
    background: 'var(--bg-surface)',
    borderLeft: '1px solid var(--border-subtle)',
    boxShadow: 'var(--shadow-lg)',
    display: 'flex',
    flexDirection: 'column',
    // Slide-in from the right.
    animation: 'drawer-slide-in 180ms ease-out',
  };
}

const drawerHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '14px 20px',
  borderBottom: '1px solid var(--border-subtle)',
  flexShrink: 0,
};
